import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Users, 
  TrendingUp, 
  Award, 
  Star,
  ChevronLeft,
  Loader2,
  User,
  Trophy
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PlayerStats() {
  const navigate = useNavigate();
  const [selectedLeague, setSelectedLeague] = useState('nhl');
  const [leagues, setLeagues] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeagues();
  }, []);

  useEffect(() => {
    if (selectedLeague) {
      fetchLeaders(selectedLeague);
    }
  }, [selectedLeague]);

  const fetchLeagues = async () => {
    try {
      const response = await fetch(`${API_URL}/api/leagues`);
      if (response.ok) {
        const data = await response.json();
        // Filter to leagues that support player stats
        const supportedLeagues = data.filter(l => 
          ['nhl', 'nba', 'nfl', 'mlb'].includes(l.league_id)
        );
        setLeagues(supportedLeagues);
      }
    } catch (err) {
      console.error('Failed to fetch leagues:', err);
    }
  };

  const fetchLeaders = async (leagueId) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/players/stats/${leagueId}`);
      if (response.ok) {
        const data = await response.json();
        setLeaders(data.leaders || []);
      }
    } catch (err) {
      console.error('Failed to fetch leaders:', err);
    } finally {
      setLoading(false);
    }
  };

  const getLeagueName = (leagueId) => {
    const league = leagues.find(l => l.league_id === leagueId);
    return league?.name || leagueId.toUpperCase();
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1">
        {/* Header */}
        <div className="border-b border-border">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center gap-2 mb-4">
              <Button 
                variant="ghost" 
                size="sm" 
                className="rounded-sm"
                onClick={() => navigate('/board')}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back to Board
              </Button>
            </div>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-500/10 rounded-sm flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h1 className="font-display text-3xl font-bold uppercase tracking-tight">
                    Player Stats
                  </h1>
                  <p className="text-muted-foreground">
                    League leaders and player statistics
                  </p>
                </div>
              </div>
              
              <Select value={selectedLeague} onValueChange={setSelectedLeague}>
                <SelectTrigger className="w-[180px] rounded-sm" data-testid="league-select">
                  <SelectValue placeholder="Select League" />
                </SelectTrigger>
                <SelectContent>
                  {leagues.map((league) => (
                    <SelectItem key={league.league_id} value={league.league_id}>
                      {league.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Loading State */}
          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-card border border-border rounded-sm p-6">
                  <Skeleton className="h-6 w-32 mb-4" />
                  <div className="space-y-3">
                    {[...Array(5)].map((_, j) => (
                      <Skeleton key={j} className="h-12 w-full" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Leaders by Category */}
          {!loading && leaders.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {leaders.map((category, idx) => (
                <div 
                  key={idx}
                  className="bg-card border border-border rounded-sm p-6"
                  data-testid={`stat-category-${idx}`}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <Trophy className="w-5 h-5 text-yellow-400" />
                    <h3 className="font-display font-bold uppercase tracking-tight">
                      {category.category}
                    </h3>
                  </div>
                  
                  <div className="space-y-3">
                    {category.leaders.map((leader, lidx) => (
                      <div 
                        key={lidx}
                        className="flex items-center gap-3 p-2 rounded-sm bg-secondary/30 hover:bg-secondary/50 transition-colors"
                      >
                        <span className={`font-mono font-bold text-lg w-6 ${
                          lidx === 0 ? 'text-yellow-400' : 
                          lidx === 1 ? 'text-zinc-400' : 
                          lidx === 2 ? 'text-orange-400' : 'text-muted-foreground'
                        }`}>
                          {lidx + 1}
                        </span>
                        
                        <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center overflow-hidden">
                          {leader.headshot ? (
                            <img 
                              src={leader.headshot} 
                              alt={leader.name}
                              className="w-10 h-10 object-cover rounded-full"
                              onError={(e) => { 
                                e.target.style.display = 'none';
                                e.target.parentNode.innerHTML = `<span class="text-xs font-bold">${leader.name?.charAt(0) || '?'}</span>`;
                              }}
                            />
                          ) : (
                            <User className="w-5 h-5 text-muted-foreground" />
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{leader.name}</p>
                          <p className="text-xs text-muted-foreground">{leader.team}</p>
                        </div>
                        
                        <div className="text-right">
                          <p className="font-mono font-bold text-green-400">{leader.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No Data */}
          {!loading && leaders.length === 0 && (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-display text-xl font-bold uppercase mb-2">
                No Stats Available
              </h3>
              <p className="text-muted-foreground mb-4">
                Player statistics for {getLeagueName(selectedLeague)} are not currently available.
              </p>
              <Button 
                variant="outline" 
                className="rounded-sm"
                onClick={() => setSelectedLeague('nhl')}
              >
                Try NHL Stats
              </Button>
            </div>
          )}

          {/* Impact Analysis Info */}
          <div className="mt-8 bg-blue-500/5 border border-blue-500/20 rounded-sm p-6">
            <div className="flex gap-3">
              <TrendingUp className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-display font-bold uppercase tracking-tight text-blue-400 mb-2">
                  Player Impact Analysis
                </h3>
                <p className="text-sm text-muted-foreground">
                  View detailed player impact analysis on individual game pages. 
                  Our system evaluates key players and their potential impact on game outcomes 
                  based on recent performance, matchup history, and statistical trends.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
